import { useEffect, useRef } from "react";

/**
 * StreakMilestone — a quiet acknowledgement of showing up, repeatedly.
 *
 * Deliberately restrained: one number, one sentence, and an explicit promise
 * that a gap does not erase any of it. Streaks must never read as debt, so
 * nothing here frames a missed day as loss, and there is no confetti, badge or
 * trophy attached to it.
 *
 * The count sits on a soft filled disc rather than inside a stroked ring. A
 * ring that closes is the activity-ring motif — the most recognisable
 * loss-aversion symbol in consumer fitness — and a circle you can complete is
 * a circle you can fail to complete. A disc has no completion state to lose.
 */

const MILESTONE_DATA = {
  7: {
    title: "One week of showing up",
    body: "Seven days. Whatever those days were like, you kept turning up for yourself.",
  },
  14: {
    title: "Two weeks in",
    body: "Fourteen days. The hardest part — starting, over and over — is behind you.",
  },
  30: {
    title: "A month of this",
    body: "Thirty days. That isn't luck. That's something taking shape.",
  },
  60: {
    title: "Two months",
    body: "Sixty days of choosing to move, including the days you didn't feel like it.",
  },
  100: {
    title: "A hundred days",
    body: "One hundred. Whatever else has been going on, you kept this one thing going.",
  },
  365: {
    title: "A full year",
    body: "Three hundred and sixty-five days. Look how far back that goes now.",
  },
};

export function shouldShowStreakMilestone(streak) {
  if (!MILESTONE_DATA[streak]) return false;
  try {
    const shown = JSON.parse(localStorage.getItem("sentinel_streak_milestones") || "[]");
    return !shown.includes(streak);
  } catch {
    return false;
  }
}

export function markStreakMilestoneShown(streak) {
  try {
    const shown = JSON.parse(localStorage.getItem("sentinel_streak_milestones") || "[]");
    if (!shown.includes(streak)) {
      localStorage.setItem(
        "sentinel_streak_milestones",
        JSON.stringify([...shown, streak])
      );
    }
  } catch {
    // ignore
  }
}

export default function StreakMilestone({ streak, onDismiss }) {
  const m = MILESTONE_DATA[streak];

  const dialogRef = useRef(null);
  const overlayRef = useRef(null);

  // Focus moves in, everything behind leaves the tab order and the a11y tree,
  // and focus goes back where it came from on close.
  useEffect(() => {
    const node = dialogRef.current;
    const previous = document.activeElement;
    node?.focus({ preventScroll: true });

    const locked = [];
    for (let el = overlayRef.current; el && el !== document.body; el = el.parentElement) {
      for (const sibling of el.parentElement?.children || []) {
        if (sibling === el || sibling.inert) continue;
        if (sibling.matches?.('[role="dialog"]') || sibling.querySelector?.('[role="dialog"]')) {
          continue;
        }
        sibling.inert = true;
        locked.push(sibling);
      }
    }

    return () => {
      for (const el of locked) el.inert = false;
      if (previous instanceof HTMLElement) previous.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  if (!m) return null;

  return (
    <div
      ref={overlayRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
      }}
    >
      {/* Tapping outside dismisses — as a real control, not a div with onClick. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="ds-fade-in"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          minHeight: 0,
          padding: 0,
          border: 0,
          borderRadius: 0,
          background: "var(--scrim)",
          cursor: "pointer",
        }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="streak-milestone-title"
        tabIndex={-1}
        ref={dialogRef}
        className="ds-settle-in"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "340px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-lg)",
          padding: "var(--space-9) var(--card-pad-lg) var(--card-pad-lg)",
          textAlign: "center",
          /* The container takes focus on mount so a screen reader lands inside
             the dialog. It is not itself a control, so it doesn't wear a ring —
             every real control in here still does. */
          outline: "none",
        }}
      >
        {/* A soft disc: no stroke, no arc, nothing to close. */}
        <div
          className="ds-fade-in"
          style={{
            width: "88px",
            height: "88px",
            margin: "0 auto var(--stack-sm)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--accent-soft)",
            borderRadius: "var(--radius-circle)",
          }}
        >
          {/* Small on purpose. A count rendered at trophy scale becomes the
              thing you're afraid to lose. */}
          <span
            className="ds-heading"
            style={{
              fontWeight: "var(--weight-light)",
              fontVariantNumeric: "tabular-nums",
              color: "var(--text2)",
            }}
          >
            {streak}
          </span>
        </div>

        <p className="ds-meta" style={{ marginBottom: "var(--stack)" }}>
          days you've shown up
        </p>

        <h2
          id="streak-milestone-title"
          className="ds-title"
          style={{ marginBottom: "var(--space-3)" }}
        >
          {m.title}
        </h2>

        <p
          className="ds-prose"
          style={{
            maxWidth: "none",
            marginBottom: "var(--stack-lg)",
            textWrap: "pretty",
          }}
        >
          {m.body}
        </p>

        {/* Two centred paragraphs at the same size, 20px apart, merged into one
            six-line block — and the promise below is a separate beat, not the
            tail of the one above. A short inset hairline and a full --stack-lg
            either side separate them without demoting either. */}
        <div
          aria-hidden="true"
          style={{
            height: "1px",
            background: "var(--border)",
            margin: "0 var(--space-10) var(--stack-lg)",
          }}
        />

        {/* The anti-debt line. A gap is never framed as loss in this product —
            so this reads at the same size as everything else, not as a
            footnote under the count. */}
        <p
          className="ds-support"
          style={{
            fontSize: "var(--text-base)",
            color: "var(--text2)",
            marginBottom: "var(--stack-lg)",
            lineHeight: "var(--leading-relaxed)",
            /* Centred text orphans its last word very visibly. */
            textWrap: "balance",
          }}
        >
          If a day gets away from you, none of this goes anywhere — it picks
          back up wherever you left it.
        </p>

        <button type="button" className="btn btn-primary btn-full" onClick={onDismiss}>
          Continue
        </button>
      </div>
    </div>
  );
}
